// DraggableBackButton.jsx

import React, { useState, useRef, useEffect } from 'react';
import { mdiArrowLeft } from '@mdi/js'; // Importa el ícono deseado de MDI
import Icon from '@mdi/react';

const DraggableBackButton = ({
                                 initialPosition = { x: 20, y: 20 }, // Posición inicial predeterminada
                                 onClick, // Función a ejecutar al hacer clic en el botón
                                 borderColor = 'border-primary', // Color del borde izquierdo
                                 bgColor = 'bg-neutral', // Color de fondo
                                 textColor = 'text-neutral-content', // Color del texto
                                 iconPath = mdiArrowLeft, // Ruta del ícono
                                 iconSize = 0.8, // Tamaño del ícono
                                 label = 'Atrás', // Texto del botón
                             }) => {
    // Estado para la posición del botón
    const [position, setPosition] = useState(initialPosition);
    const [isDragging, setIsDragging] = useState(false);
    const [rel, setRel] = useState({ x: 0, y: 0 }); // Posición relativa al iniciar el drag
    const buttonRef = useRef(null);
    const dragTimeout = useRef(null);

    // Maneja el evento de presionar el botón (Mouse)
    const onMouseDown = (e) => {
        // Solo responder al botón izquierdo del ratón
        if (e.button !== 0) return;

        const rect = buttonRef.current.getBoundingClientRect();
        setRel({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });

        // Inicia un temporizador para iniciar el drag después de 300ms
        dragTimeout.current = setTimeout(() => {
            setIsDragging(true);
        }, 300);

        e.stopPropagation();
        e.preventDefault();
    };

    // Maneja el evento de tocar el botón (Touch)
    const onTouchStart = (e) => {
        const touch = e.touches[0];
        const rect = buttonRef.current.getBoundingClientRect();
        setRel({
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top,
        });

        // Inicia un temporizador para iniciar el drag después de 300ms
        dragTimeout.current = setTimeout(() => {
            setIsDragging(true);
        }, 300);

        e.stopPropagation();
        e.preventDefault();
    };

    // Maneja el movimiento del ratón (solo eje Y)
    const onMouseMove = (e) => {
        if (!isDragging) return;
        setPosition((prev) => ({
            x: prev.x, // Mantiene la posición X fija
            y: e.clientY - rel.y,
        }));
        e.stopPropagation();
        e.preventDefault();
    };

    // Maneja el movimiento táctil (solo eje Y)
    const onTouchMove = (e) => {
        if (!isDragging) return;
        const touch = e.touches[0];
        setPosition((prev) => ({
            x: prev.x, // Mantiene la posición X fija
            y: touch.clientY - rel.y,
        }));
        e.stopPropagation();
        e.preventDefault();
    };

    // Maneja el evento de soltar el ratón
    const onMouseUp = (e) => {
        if (isDragging) {
            setIsDragging(false);
        }
        // Limpia el temporizador si el drag no comenzó
        clearTimeout(dragTimeout.current);
        e.stopPropagation();
        e.preventDefault();
    };

    // Maneja el evento de terminar el toque
    const onTouchEnd = (e) => {
        if (isDragging) {
            setIsDragging(false);
        }
        // Limpia el temporizador si el drag no comenzó
        clearTimeout(dragTimeout.current);
        e.stopPropagation();
        e.preventDefault();
    };

    // Añade y remueve los event listeners según el estado de dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
            document.addEventListener('touchmove', onTouchMove, { passive: false });
            document.addEventListener('touchend', onTouchEnd);
        } else {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        }

        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };
    }, [isDragging]);

    return (
        <button
            ref={buttonRef}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            onClick={onClick}
            className={`z-50 absolute ${bgColor} ${textColor} pl-3 pr-4 py-2 rounded-l-full border-l-4 ${borderColor} shadow-lg hover:brightness-110 active:scale-95 transition-all duration-150 cursor-pointer flex items-center gap-2 text-sm font-medium`}
            style={{
                top: position.y,
                right: position.x,
                userSelect: 'none',
                touchAction: 'none',
            }}
            aria-label={label}
        >
            {/* Ícono de Material Design Icons */}
            <Icon path={iconPath} size={iconSize} />
            {/* Texto que se oculta en pantallas pequeñas */}
            <span className={`${isDragging ? 'inline': "hidden"} sm:inline`}>{label}</span>
        </button>
    );
};

export default DraggableBackButton;
